import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CalendarClock, Loader2, Mail, MapPin, Phone, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MaskedLines, Reveal } from "../components/motion";
import { api } from "../lib/api";
import { usePageTitle } from "../lib/title";

const emptyForm = { name: "", email: "", phone: "", company: "", team_size: "", preferred_time: "", message: "" };

const inputCls =
  "rounded-none border-border bg-cream px-4 py-3 text-sm text-ink placeholder:text-ink/35 focus-visible:ring-1 focus-visible:ring-forest focus-visible:ring-offset-0";

export default function Contact() {
  const [searchParams] = useSearchParams();
  usePageTitle("Contact & Consultations — The Woodlands, TX");
  const requested = searchParams.get("type");
  const [type, setType] = useState(
    ["consultation", "medicare-list"].includes(requested) ? requested : "contact");
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", { ...form, type });
      toast.success(
        type === "consultation"
          ? "Consultation requested — I'll reach out within one business day."
          : type === "medicare-list"
            ? "You're on the Medicare list — I'll follow up personally when I'm ready to help."
            : "Message sent — thank you for reaching out."
      );
      setForm(emptyForm);
    } catch (err) {
      toast.error("Something went wrong sending your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="mx-auto max-w-7xl px-6 pb-24 pt-36 lg:px-10 lg:pt-44 lg:pb-32">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <h1 className="font-mono text-xs uppercase tracking-[0.35em] text-terra" data-testid="contact-kicker">
              Contact Kyle Walker · The Woodlands, TX
            </h1>
            <MaskedLines
              as="p"
              className="mt-8 font-serif text-5xl font-light leading-[0.95] tracking-tighter text-forest sm:text-6xl"
              lines={["Start the", "conversation."]}
            />
            <Reveal delay={0.4}>
              <p className="mt-8 max-w-md text-base leading-relaxed text-ink/70">
                Whether it's a quick question or a full benefits review for your family or your team, the first
                step is a simple message. Consultations cost you nothing (carriers pay my commission), and
                they&rsquo;re unhurried and jargon-free.
              </p>
              <div className="mt-12 space-y-6" data-testid="contact-details">
                <div className="flex items-start gap-4">
                  <MapPin className="mt-1 h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50">Service Area</p>
                    <p className="mt-1 text-sm text-ink/80">Based in The Woodlands, Texas — serving The Woodlands, Houston and surrounding communities, in person or virtually</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Mail className="mt-1 h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50">Email</p>
                    <a
                      href="mailto:kyle@ohcbenefits.com"
                      className="link-underline mt-1 inline-block text-sm text-ink/80"
                      data-testid="contact-email-link"
                    >
                      kyle@ohcbenefits.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone className="mt-1 h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50">Phone</p>
                    <a
                      href="tel:+17137257413"
                      className="link-underline mt-1 inline-block text-sm text-ink/80"
                      data-testid="contact-phone-link"
                    >
                      (713) 725-7413
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CalendarClock className="mt-1 h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50">Response Time</p>
                    <p className="mt-1 text-sm text-ink/80">Within one business day, usually sooner</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal className="lg:col-span-6 lg:col-start-7" delay={0.2}>
            <form
              onSubmit={submit}
              className="border border-border bg-sand/40 p-8 lg:p-10"
              data-testid="contact-form"
            >
              <div className="flex gap-2" data-testid="contact-type-toggle">
                {[
                  ["contact", "General Question"],
                  ["consultation", "Request a Consultation"],
                  ["medicare-list", "Medicare – Join the List"],
                ].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setType(value)}
                    data-testid={`contact-type-${value}`}
                    className={`rounded-full px-5 py-2 text-xs font-medium tracking-wide transition-all duration-300 ${
                      type === value
                        ? "bg-forest text-cream"
                        : "border border-border text-ink/60 hover:border-gold hover:text-terra"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="mt-8 grid gap-6 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="lead-name" className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/60">
                    Full Name *
                  </Label>
                  <Input
                    id="lead-name"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Jane Doe"
                    className={inputCls}
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lead-email" className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/60">
                    Email *
                  </Label>
                  <Input
                    id="lead-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="jane@example.com"
                    className={inputCls}
                    data-testid="contact-email-input"
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="lead-phone" className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/60">
                    Phone (optional)
                  </Label>
                  <Input
                    id="lead-phone"
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="(512) 555-0100"
                    className={inputCls}
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>

              <AnimatePresence initial={false}>
                {type === "consultation" && (
                  <motion.div
                    key="consultation-fields"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                    data-testid="consultation-fields"
                  >
                    <div className="grid gap-6 pt-6 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="lead-company" className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/60">
                          Company (if employer)
                        </Label>
                        <Input
                          id="lead-company"
                          value={form.company}
                          onChange={set("company")}
                          placeholder="Acme Co."
                          className={inputCls}
                          data-testid="contact-company-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lead-team" className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/60">
                          Who's it for?
                        </Label>
                        <select
                          id="lead-team"
                          value={form.team_size}
                          onChange={set("team_size")}
                          className="h-10 w-full rounded-none border border-border bg-cream px-4 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-forest"
                          data-testid="contact-team-size-select"
                        >
                          <option value="">Select…</option>
                          <option value="individual">Just me / my family</option>
                          <option value="2-10">Team of 2–10</option>
                          <option value="11-50">Team of 11–50</option>
                          <option value="50+">Team of 50+</option>
                        </select>
                      </div>
                      <div className="space-y-2 sm:col-span-2">
                        <Label htmlFor="lead-time" className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/60">
                          Preferred time to talk
                        </Label>
                        <Input
                          id="lead-time"
                          value={form.preferred_time}
                          onChange={set("preferred_time")}
                          placeholder="e.g. Weekday mornings, Tuesday afternoon…"
                          className={inputCls}
                          data-testid="contact-preferred-time-input"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="mt-6 space-y-2">
                <Label htmlFor="lead-message" className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/60">
                  {type === "consultation"
                    ? "What would you like to cover? *"
                    : type === "medicare-list"
                      ? "Anything I should know? *"
                      : "Your Message *"}
                </Label>
                <Textarea
                  id="lead-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder={
                    type === "consultation"
                      ? "Tell me a little about your situation — current coverage, family or team size, and what's on your mind."
                      : type === "medicare-list"
                        ? "When you turn 65, and anything you'd like me to look into when Medicare is ready."
                        : "How can I help?"
                  }
                  className={`${inputCls} resize-none`}
                  data-testid="contact-message-input"
                />
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="group mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-forest px-8 py-4 text-sm font-medium text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-moss disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    {type === "consultation"
                      ? "Request Consultation"
                      : type === "medicare-list"
                        ? "Join the List"
                        : "Send Message"}
                    <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </>
                )}
              </button>
              <p className="mt-5 text-xs leading-relaxed text-ink/50" data-testid="contact-privacy-note">
                Your information is used only to respond to your enquiry. No spam, no list-selling — ever.
              </p>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
